import React, { useEffect, useState } from 'react';
import {
  UsersIcon,
  SearchBar,
} from '../components';
import { Skeleton } from '../components/ui/skeleton';
import { fetchFavorites, toggleFavorite } from '../utilities/api';
import FavoritesCard from '../components/FavoritesCard';
import {
  mapCultureCustomsListData,
  mapPrayerRitesListData,
  mapCatecheticalCoursesListData,
  mapSingleDevotionsListData,
  mapSingleMeditationData
} from '../utilities';
import { toast } from 'react-toastify';
import { Select, SelectContent, SelectTrigger, SelectValue } from "../components/ui/select";
const PageFavorites = () => {
  const [favorites, setFavorites] = useState({ items: [] });
  const [sortOptions, setSortOptions]=useState("last-added")
  const [categoryFilter , setCategoryFilter]= useState("all")
  const [isLoading, setIsLoading] = useState(true);
  const [searchItems, setSearchItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [skeletonCount, setSkeletonCount] = useState(8);
  const normalizeContentType = (rawType = '') => {
    const base = rawType.replace(/^ps-/, '');
    const singularToPluralMap = {
      'catechesis': 'catecheses',
      'devotion': 'devotions',
      'prayer-rite': 'prayer-rites',
      'culture-custom': 'culture-customs',
      'catechetical-course': 'catechetical-courses',
      'meditation': 'meditations',
    };
    return singularToPluralMap[base] || base;
  };
  useEffect(() => {
    const loadFavorites = async () => {
      try {
        setIsLoading(true);
        const response = await fetchFavorites();
        const data = response?.data || [];
        const included = response?.included || [];
        setSkeletonCount(data.length > 0 ? data.length : 4);
        const transformed = data.map((fav) => {
          const type = fav?.type || '';
          const contentType = normalizeContentType(type);
          const isTaxonomy = ['ps-catechetical-courses', 'ps-seasons'].includes(type);
          let item = null;
          if (type === 'ps-culture-customs') {
            item = mapCultureCustomsListData([fav], included)[0];
          } else if (type === 'ps-prayer-rites') {
            item = mapPrayerRitesListData([fav], included)[0];
          } else if (type === 'ps-catechetical-courses') {
            item = mapCatecheticalCoursesListData([fav], included)[0];
          } else if (type === 'ps-devotions') {
            const devotionItems = mapSingleDevotionsListData([fav], included);
            if (Array.isArray(devotionItems) && devotionItems.length > 0) {
              item = devotionItems[0];
            } else {
              console.log('Devotion mapping failed:', fav);
              return null; // skip rendering this favorite
            }
          } else if (type === 'ps-meditations') {
            item = mapSingleMeditationData({ data: fav, included });
            if (!item) {
              console.log('meditation mapping failed:', fav);
              return null;
            }
          }
          
          if (!item) return null;
          let readableTime = undefined;
          if (type === 'ps-catechetical-courses') {
            const estimatedTimeInSeconds = parseInt(fav.attributes?.["ps-estimated-time"]?.[0] || "0", 10);
            const hours = Math.floor(estimatedTimeInSeconds / 3600);
            const minutes = Math.round((estimatedTimeInSeconds % 3600) / 60);

            if (hours > 0) {
              readableTime = `About ${hours} hr${hours > 1 ? 's' : ''}`;
            } else if (minutes > 0) {
              readableTime = `About ${minutes} min`;
            } else {
              readableTime = "About 0 min";
            }
          }
          return {
            id: fav.id,
            title: fav?.attributes?.title || 'Untitled',
            catList: fav?.attributes?.catList,
            imageUrl: item.imageUrl,
            slug: item?.slug,
            count: item.count,
            aboutHours: item.aboutHours || readableTime,
            catechesis: item.catechesis,
            description: item.description,
            contentType,
            isTaxonomy,
            favorite: true
          };
        }).filter(Boolean);
        setFavorites({ items: transformed });
        setSearchItems(transformed.map(item => ({ id: item.id, name: item.title })));
      } catch (error) {
        console.error("Error loading favorites:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadFavorites();
  }, []);
  const handleToggleFavorite = async (id) => {
    const item = favorites.items.find(fav => fav.id === id);
    if (!item) return;
    try {
      setFavorites(prev => ({ items: prev.items.filter(f => f.id !== id) }));
      await toggleFavorite(item.contentType, id, item.isTaxonomy);
      toast.success("Removed from favorites", {
        icon: <span className="custom-toast-icon success" />,
      });
    } catch (error) {
      console.error(`Failed to remove favorite for ID ${id}:`, error);
      setFavorites(prev => ({ items: [item, ...prev.items] }));
      toast.error("Failed to remove from favorites");
    }
  };
  const handleSearchQuery = (value) => {
    setSearchQuery(value);
  };
  const filteredFavorites = searchQuery
    ? favorites.items.filter(item => item.title.toLowerCase().includes(searchQuery.toLowerCase()))
    : favorites.items;
  return (
    <div className='w-full'>
      <div className='px-4 py-8 pl-6 xl:px-6 sm:px-6'>
        <div className='mb-4 md:mb-6'>
          <div className='flex flex-wrap items-center justify-between gap-6 md:gap-3'>
            <div className="flex items-center gap-2">
              <span className='w-6 h-6 mt-px text-gold-200'>
                <UsersIcon className='w-full h-full text-gold-600' />
              </span>
              <h1 className='text-2xl font-bold leading-7 text-gray-900 md:text-xl xl:text-2xl font-cmr'>Favorites</h1>
            </div>
            <SearchBar
              inputName="s"
              inputPlaceholder="Search by name"
              items={searchItems}
              value={searchQuery}
              onSearch={handleSearchQuery}
            />
          </div>
        </div>
        <div className='flex flex-wrap gap-3 mt-4 mb-8'>
           <Select value={sortOptions} onValueChange={setSortOptions}>
            <SelectTrigger name='Sort by'>
              <SelectValue placeholder="Last added"/>
            </SelectTrigger>
            <SelectContent>

            </SelectContent>
           </Select>
           <Select>
            <SelectTrigger name='Categories'>
              <SelectValue placeholder="All"/>
            </SelectTrigger>
            <SelectContent>

            </SelectContent>
           </Select>
        </div>
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(skeletonCount)].map((_, index) => (
              <Skeleton key={index} className="h-[100px] w-full rounded-lg" />
            ))}
          </div>
        ) : filteredFavorites.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredFavorites.map(item => (
              <FavoritesCard key={item.id} item={item} onToggleFavorite={handleToggleFavorite} />
            ))}
          </div>
        ) : (
          <div className='flex flex-col items-center justify-center my-60 mr-20 text-center text-gray-500'>
            <img src={`${process.env.PUBLIC_URL}/media/images/magnifying.svg`} alt='No favorites' className='w-[94px] h-[94px] mb-4' />
            <h2 className='text-base font-bold font-redhat text-gray-800'>
              No favorites yet!
            </h2>
            <p className='mt-1 text-gray-400 font-redhat text-sm'>
              Save your favorite items here for easy access.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
export default PageFavorites;
